import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, LineChart, Landmark } from "lucide-react";

const BOARDROOM = "/photo-boardroom.jpg";

const pillars = [
  {
    icon: Compass,
    title: "Strategic Decision-Making",
    body: "Frameworks for decisive action in the face of ambiguity, and the leadership to own the outcome.",
  },
  {
    icon: LineChart,
    title: "Financial Understanding",
    body: "Reading the numbers behind the narrative: statements, capital, value, and the questions a director must ask.",
  },
  {
    icon: Landmark,
    title: "Governance Readiness",
    body: "Fiduciary duty, board dynamics, and the language of oversight — demystified before you take the seat.",
  },
];

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] } },
});

export const Program = () => (
  <section id="program" className="relative py-28 lg:py-40 bg-[#0E0E0E]" data-testid="program-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div variants={fadeUp()} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="max-w-3xl">
        <p className="text-[0.65rem] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">The Flagship Program</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F5F5F5]" data-testid="program-heading">
          SHE Global™ Boardroom <em className="italic text-[#D4AF37]">Accelerator</em>
        </h2>
        <p className="mt-8 text-base sm:text-lg leading-relaxed text-white" data-testid="program-intro">
          A single, focused program in development — built to prepare experienced women for
          board-level responsibility and entrepreneurial leadership. Three disciplines form its core.
        </p>
        <Link
          to="/accelerator"
          data-testid="program-explore-curriculum-link"
          className="inline-flex items-center gap-2 mt-8 text-xs tracking-[0.3em] uppercase text-[#D4AF37] hover:text-[#C5A24D] transition-colors duration-300"
        >
          Explore the curriculum <ArrowRight size={14} />
        </Link>
      </motion.div>

      <div className="mt-16 grid md:grid-cols-3 gap-5 auto-rows-fr">
        {pillars.map((p, i) => (
          <motion.div
            key={p.title}
            variants={fadeUp(0.1 + i * 0.12)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            whileHover={{ y: -6 }}
            className="group border border-white/10 bg-[#141414] p-10 flex flex-col hover:border-[#D4AF37]/50 transition-colors duration-500"
            data-testid={`program-pillar-${i + 1}`}
          >
            <p.icon className="text-[#D4AF37] mb-8" size={30} strokeWidth={1.25} />
            <h3 className="font-serif-display text-2xl text-[#F5F5F5] mb-4">{p.title}</h3>
            <p className="text-sm leading-relaxed text-white">{p.body}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-5 grid md:grid-cols-5 gap-5">
        <motion.div
          variants={fadeUp(0.15)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="md:col-span-3 relative overflow-hidden group min-h-[320px]"
          data-testid="program-boardroom-image"
        >
          <img
            src={BOARDROOM}
            alt="An empty boardroom awaiting its next members"
            className="absolute inset-0 w-full h-full object-cover grayscale-[0.4] group-hover:scale-105 transition-transform duration-[1.2s] ease-out"
          />
          <div className="absolute inset-0 bg-black/45" />
          <div className="absolute bottom-0 left-0 p-10">
            <p className="font-serif-display italic text-2xl sm:text-3xl text-[#F5F5F5]">
              "The seat is not the starting point. <span className="text-[#D4AF37]">Preparation is."</span>
            </p>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp(0.25)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="md:col-span-2 border border-[#D4AF37]/40 bg-[#141414] p-10 flex flex-col justify-between min-h-[320px]"
          data-testid="program-status-card"
        >
          <div>
            <p className="text-[0.65rem] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Program Status</p>
            <h3 className="font-serif-display text-3xl text-[#F5F5F5] leading-snug">In active development</h3>
            <p className="mt-5 text-sm leading-relaxed text-white">
              Curriculum, structure, and delivery format are being shaped now. Curriculum details,
              dates, and fees will be published only when finalized.
            </p>
          </div>
          <button
            onClick={() => document.querySelector("#interest")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="program-express-interest-btn"
            className="mt-8 self-start text-xs tracking-[0.3em] uppercase border border-[#D4AF37] text-[#D4AF37] px-8 py-4 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
          >
            Express Interest
          </button>
        </motion.div>
      </div>
    </div>
  </section>
);
